import { bool, func, string } from "prop-types";
import styled from "styled-components/macro";
import { rem } from "polished";
import { FontAwesomeIcon as Icon } from "@fortawesome/react-fontawesome";
import { faExternalLink } from "@fortawesome/free-solid-svg-icons";
import Text from "./Text";
import Label from "./Label";
import { useEffect, useRef } from "react";

function WysiwygLinkModal({
  onClose,
  onLinkInput,
  onSaveLink,
  linkUrl,
  isOpen,
}) {
  const linkModalRef = useRef();
  const linkInputRef = useRef();

  useEffect(() => {
    const isClickOutside = event => {
      if (
        linkModalRef.current &&
        !linkModalRef.current.contains(event.target)
      ) {
        onClose();
      }
    };

    document.addEventListener("click", isClickOutside, true);
    return () => {
      document.removeEventListener("click", isClickOutside, true);
    };
  }, [onClose]);

  useEffect(() => {
    if (isOpen && !!linkInputRef.current) {
      linkInputRef.current.focus();
    }
  }, [isOpen]);

  const handleEnterKey = e => {
    if (e.key === "Enter") {
      e.preventDefault();
      onSaveLink();
    }
  };

  return (
    <ModalArea isLinkActive={isOpen}>
      <LinkInputArea ref={linkModalRef}>
        <Label htmlFor="link" srOnly>
          Enter link:
        </Label>
        <Text
          id="link"
          value={linkUrl}
          onChange={onLinkInput}
          onKeyDown={handleEnterKey}
          placeholder="https://example.com"
          inputRef={linkInputRef}
        />
        {linkUrl && (
          <a
            href={linkUrl}
            className="external-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Icon icon={faExternalLink} />
          </a>
        )}
        <button onClick={onSaveLink} type="button">
          Save
        </button>
      </LinkInputArea>
    </ModalArea>
  );
}

WysiwygLinkModal.propTypes = {
  onClose: func.isRequired,
  onLinkInput: func.isRequired,
  onSaveLink: func.isRequired,
  linkUrl: string.isRequired,
  isOpen: bool.isRequired,
  showEdit: bool,
};

const ModalArea = styled.div`
  align-items: center;
  display: ${props => (props.isLinkActive ? "flex" : "none")};
  justify-content: center;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
`;

const LinkInputArea = styled.div`
  align-items: center;
  background: #ffffff;
  border-radius: ${rem("5px")};
  color: ${({ theme }) => theme.text.wysiwygToolbarColor};
  display: flex;
  padding: ${rem("8px")} ${rem("12px")};
  font-size: ${rem("13px")};

  label,
  input {
    font-size: ${rem("16px")};
    text-transform: none;
  }

  label {
    margin-bottom: 0;
    min-width: ${rem("60px")};
  }

  input {
    background: #ffffff;
    border-radius: ${rem("5px")} 0 0 ${rem("5px")};
    color: ${({ theme }) => theme.text.wysiwygToolbarColor};
    height: ${rem("30px")};
  }

  button {
    border: ${rem("1px")} solid
      ${({ readOnly, theme }) =>
        readOnly ? theme.text.borderColorReadOnly : theme.text.borderColor};
    border-left: none;
    border-radius: 0 ${rem("5px")} ${rem("5px")} 0;
    background: transparent;
    color: #06c;
    cursor: pointer;
    height: ${rem("30px")};
    line-height: 1;
    padding-right: ${rem("14px")};
    min-width: ${rem("40px")};
  }

  .external-link {
    border: ${rem("1px")} solid
      ${({ readOnly, theme }) =>
        readOnly ? theme.text.borderColorReadOnly : theme.text.borderColor};
    border-left: 0;
    border-radius: 0;
    background: transparent;
    color: #06c;
    cursor: pointer;
    height: ${rem("30px")};
    line-height: 1;
    padding: ${rem("8px")} ${rem("12px")};
    min-width: ${rem("40px")};
  }
`;

export default WysiwygLinkModal;
